import { supabase } from '@/lib/supabaseClient';
import {
  PigArrivalRecord,
  PigArrivalItemRecord,
  SupplierRecord,
} from '@/types/database';
import { documentService } from '@/services/documentService';

export interface CreateArrivalPayload {
  cycle_id: string;
  supplier_id?: string | null;
  arrival_date: string;
  head_count: number;
  total_weight_kg?: number | null;
  base_price_per_head?: number;
  base_amount?: number;
  excess_weight_rate?: number;
  excess_weight_amount?: number;
  trucking_cost?: number;
  total_cost?: number;
  delivery_receipt_no?: string | null;
  vehicle_plate_no?: string | null;
  driver_name?: string | null;
  notes?: string | null;
}

export interface ArrivalItemInput {
  sequence_no: number;
  weight_kg: number;
  tag_no?: string | null;
  pen_id?: string | null;
  health_status?: string | null;
  notes?: string | null;
}

export const arrivalService = {
  /**
   * Fetches all non-void arrivals for a given cycle with their scale items and documents.
   */
  async getArrivalsByCycle(cycleId: string): Promise<PigArrivalRecord[]> {
    const { data, error } = await supabase
      .from('pig_arrivals')
      .select(`
        *,
        suppliers (id, supplier_name, supplier_code),
        pig_arrival_items (*)
      `)
      .eq('cycle_id', cycleId)
      .eq('is_void', false)
      .order('arrival_date', { ascending: true });

    if (error) {
      console.error('[arrivalService.getArrivalsByCycle] Error:', error.message);
      throw error;
    }

    const arrivals = (data || []) as PigArrivalRecord[];

    // Fetch documents for each arrival
    for (const arrival of arrivals) {
      try {
        const docs = await documentService.getEntityDocuments('pig_arrivals', arrival.id);
        arrival.documents = docs;
      } catch (e) {
        arrival.documents = [];
      }
      if (arrival.pig_arrival_items) {
        arrival.pig_arrival_items = arrival.pig_arrival_items
          .filter((item) => !item.is_void)
          .sort((a, b) => a.sequence_no - b.sequence_no);
      }
    }

    return arrivals;
  },

  /**
   * Fetches active suppliers for the supplier select dropdown.
   */
  async getSuppliers(): Promise<SupplierRecord[]> {
    const { data, error } = await supabase
      .from('suppliers')
      .select('id, supplier_code, supplier_name, supplier_type, is_active')
      .eq('is_void', false)
      .eq('is_active', true)
      .order('supplier_name', { ascending: true });

    if (error) {
      console.warn('[arrivalService.getSuppliers] Warn:', error.message);
      return [];
    }
    return (data || []) as SupplierRecord[];
  },

  /**
   * Creates a new pig arrival record, optional individual scale items, and optional attached document.
   * Total intake weight is optional; can be provided directly, auto-derived from items, or omitted as NULL.
   */
  async createArrival(
    payload: CreateArrivalPayload,
    items?: ArrivalItemInput[],
    attachmentFile?: File | null
  ): Promise<PigArrivalRecord> {
    if (!payload.cycle_id) {
      throw new Error('Cycle ID is required.');
    }
    if (!payload.arrival_date) {
      throw new Error('Arrival date is required.');
    }
    if (!payload.head_count || payload.head_count <= 0) {
      throw new Error('Head count must be greater than zero.');
    }

    // Weight Evidence Rule: Total intake weight is optional
    let calculatedWeight: number | null = null;
    if (payload.total_weight_kg !== undefined && payload.total_weight_kg !== null && (payload.total_weight_kg as any) !== '') {
      const numWeight = Number(payload.total_weight_kg);
      if (numWeight > 0) {
        calculatedWeight = numWeight;
      } else {
        throw new Error('Total intake weight (kg) must be greater than zero if entered.');
      }
    } else if (items && items.length > 0) {
      const itemsSum = items.reduce((sum, it) => sum + (Number(it.weight_kg) || 0), 0);
      if (itemsSum > 0) {
        calculatedWeight = itemsSum;
      }
    }

    // Calculate total cost if not explicitly provided
    const baseAmt = Number(payload.base_amount) || (Number(payload.base_price_per_head) || 0) * payload.head_count;
    const excessAmt = Number(payload.excess_weight_amount) || 0;
    const trucking = Number(payload.trucking_cost) || 0;
    const totalCost = Number(payload.total_cost) || (baseAmt + excessAmt + trucking);

    const arrivalInsert: any = {
      cycle_id: payload.cycle_id,
      supplier_id: payload.supplier_id || null,
      arrival_date: payload.arrival_date,
      head_count: payload.head_count,
      total_weight_kg: calculatedWeight,
      base_price_per_head: Number(payload.base_price_per_head) || 0,
      base_amount: baseAmt,
      excess_weight_rate: Number(payload.excess_weight_rate) || 0,
      excess_weight_amount: excessAmt,
      trucking_cost: trucking,
      total_cost: totalCost,
      delivery_receipt_no: payload.delivery_receipt_no || null,
      vehicle_plate_no: payload.vehicle_plate_no || null,
      driver_name: payload.driver_name || null,
      notes: payload.notes || null,
    };

    const { data: arrival, error: arrivalErr } = await supabase
      .from('pig_arrivals')
      .insert(arrivalInsert)
      .select('*')
      .single();

    if (arrivalErr || !arrival) {
      console.error('[arrivalService.createArrival] Insert error:', arrivalErr?.message);
      throw new Error(`Failed to create pig arrival: ${arrivalErr?.message}`);
    }

    // Insert items if present
    if (items && items.length > 0) {
      const itemsToInsert = items.map((item, idx) => ({
        arrival_id: arrival.id,
        sequence_no: item.sequence_no || idx + 1,
        weight_kg: Number(item.weight_kg),
        tag_no: item.tag_no || null,
        pen_id: item.pen_id || null,
        health_status: item.health_status || 'healthy',
        notes: item.notes || null,
      }));

      const { error: itemsErr } = await supabase
        .from('pig_arrival_items')
        .insert(itemsToInsert);

      if (itemsErr) {
        console.error('[arrivalService.createArrival] Items insert error:', itemsErr.message);
      }
    }

    // Upload attachment if provided
    if (attachmentFile) {
      try {
        await documentService.uploadDocument({
          file: attachmentFile,
          fileName: attachmentFile.name,
          entityType: 'pig_arrivals',
          entityId: arrival.id,
          documentType: 'weighing_sheet',
          notes: 'Paper weighing sheet slip',
        });
      } catch (docErr: any) {
        console.warn('[arrivalService.createArrival] File upload warning:', docErr.message);
      }
    }

    return arrival as PigArrivalRecord;
  },
};
